import { Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { PortalBackendService } from '../avaliacoes/services/portal-backend.service';
import { AlunoComAnosLetivos, AnosLetivos } from '../avaliacoes/models/portal.models';
import { MatriculaService } from '../../services/matricula.service';
import { BoletoVisualizacao, FinanceiroService } from './services/financeiro.service';

export interface ResumoFinanceiro {
  totalAPagar: number;
  totalPago: number;
  saldoEmAberto: number;
  saldoAtrasado: number;
}

export enum ContaParcelaStatusPagamento {
  Ativa = 0,
  Quitada = 1,
  Cancelada = 2,
  Estornado = 3,
  Retido = 4,
  ErroPagamento = 5
}

export interface ContaReceberPortal {
  contaParcelaId: number;
  descricao: string;
  dataVencimento: string;
  dataPagamento: string | null;
  valor: number;
  valorPago: number;
  statusPagamento: ContaParcelaStatusPagamento;
  nomeResponsavelFinanceiro: string;
  boletoId: number;
  notaFiscalId: number | null;
  statusProcessamentoNotaFiscal: string | null;
  linkNotaFiscalPrefeitura: string | null;
}

type CorIconeBoleto = 'Azul' | 'Verde' | 'Cinza Claro';

export interface PortalPaisFinanceiroResponse {
  resumo: ResumoFinanceiro;
  contas: ContaReceberPortal[];
}

@Component({
  selector: 'app-financeiro',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, CurrencyPipe],
  templateUrl: './financeiro.html',
  styleUrl: './financeiro.scss'
})
export class FinanceiroComponent implements OnInit {
  alunos: AlunoComAnosLetivos[] = [];
  alunoSelecionado: AlunoComAnosLetivos | null = null;
  anosLetivos: AnosLetivos[] = [];
  anoSelecionado: AnosLetivos | null = null;
  todasParcelasSelecionado = false;

  resumo: ResumoFinanceiro | null = null;
  contas: ContaReceberPortal[] = [];
  contasSelecionadas = new Set<number>();
  carregandoFinanceiro = false;
  erroFinanceiro = false;

  constructor(
    private backendService: PortalBackendService,
    private matriculaService: MatriculaService,
    private financeiroService: FinanceiroService
  ) {}

  ngOnInit() {
    this.carregarAlunos();
  }

  carregarAlunos() {
    this.backendService.getAlunosComAnosLetivos().subscribe(response => {
      const alunosBackend = (response.body ?? []) as AlunoComAnosLetivos[];
      this.alunos = this.agruparAlunosPorId(alunosBackend);

      if (this.alunos.length > 0) {
        this.selecionarAluno(this.alunos[0]);
      }
    });
  }

  selecionarAluno(aluno: AlunoComAnosLetivos) {
    this.todasParcelasSelecionado = false;
    this.alunoSelecionado = aluno;
    this.anosLetivos = [];
    this.anoSelecionado = null;
    this.resumo = null;
    this.contas = [];
    this.contasSelecionadas.clear();

    this.matriculaService.buscarAnosLetivos(aluno.id).subscribe({
      next: (response) => {
        if (this.todasParcelasSelecionado || this.alunoSelecionado?.id !== aluno.id) {
          return;
        }

        const anosLetivosDb = (response.body ?? []).map((ano: any) => ({
          id: ano.anoLetivoId,
          descricao: ano.descricao
        }));
        this.anosLetivos = this.ordenarAnosLetivos(anosLetivosDb);

        if (this.anosLetivos.length > 0) {
          this.anoSelecionado = this.anosLetivos[0];
          this.carregarFinanceiro();
        }
      },
      error: (err) => {
        console.error('Erro ao buscar anos letivos do aluno:', err);
      }
    });
  }

  selecionarTodasParcelas() {
    if (this.alunos.length === 0) return;

    const anoSelecionadoId = this.anoSelecionado?.id;
    this.todasParcelasSelecionado = true;
    this.anosLetivos = this.ordenarAnosLetivos(
      this.alunos.flatMap(aluno =>
        (aluno.anosLetivos ?? []).map(ano => ({
          id: ano.id,
          descricao: ano.descricao
        }))
      )
    );
    this.anoSelecionado = this.anosLetivos.find(ano => ano.id === anoSelecionadoId)
      ?? this.anosLetivos[0]
      ?? null;

    if (!this.anoSelecionado) {
      this.resumo = null;
      this.contas = [];
      this.contasSelecionadas.clear();
      return;
    }

    this.carregarFinanceiroTodasParcelas();
  }

  onAnoChange() {
    if (this.todasParcelasSelecionado) {
      this.carregarFinanceiroTodasParcelas();
      return;
    }

    this.carregarFinanceiro();
  }

  carregarFinanceiro() {
    if (!this.alunoSelecionado || !this.anoSelecionado) return;

    this.carregandoFinanceiro = true;
    this.erroFinanceiro = false;
    this.resumo = null;
    this.contas = [];
    this.contasSelecionadas.clear();

    this.financeiroService.buscarFinanceiro(this.alunoSelecionado.id, this.anoSelecionado.id).subscribe({
      next: (response: HttpResponse<any>) => {
        const data = response.body as PortalPaisFinanceiroResponse;
        this.resumo = data.resumo;
        this.contas = data.contas;
        this.carregandoFinanceiro = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao buscar financeiro:', err);
        this.carregandoFinanceiro = false;
        this.erroFinanceiro = true;
      }
    });
  }

  carregarFinanceiroTodasParcelas() {
    if (!this.anoSelecionado || this.alunos.length === 0) return;

    const alunoIds = Array.from(new Set(
      this.alunos
        .map(aluno => aluno.id)
        .filter(id => id > 0)
    ));

    if (alunoIds.length === 0) return;

    this.carregandoFinanceiro = true;
    this.erroFinanceiro = false;
    this.resumo = null;
    this.contas = [];
    this.contasSelecionadas.clear();

    this.financeiroService.buscarFinanceiroTodasParcelas(alunoIds, this.anoSelecionado.id).subscribe({
      next: (response: HttpResponse<any>) => {
        const data = response.body as PortalPaisFinanceiroResponse;
        this.resumo = data.resumo;
        this.contas = data.contas;
        this.carregandoFinanceiro = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao buscar financeiro consolidado:', err);
        this.carregandoFinanceiro = false;
        this.erroFinanceiro = true;
      }
    });
  }

  abrirNotaFiscal(link: string | null) {
    if (link) {
      window.open(link, '_blank');
    }
  }

  boletoDisponivel(conta: ContaReceberPortal): boolean {
    return conta.boletoId > 0;
  }

  abrirBoleto(conta: ContaReceberPortal) {
    if (!this.boletoDisponivel(conta) || !this.anoSelecionado) {
      return;
    }

    const alunoIds = this.obterAlunoIdsConsultaAtual();

    if (alunoIds.length === 0) {
      return;
    }

    const boletoWindow = window.open('', '_blank');

    if (!boletoWindow) {
      alert('O bloqueador de pop-ups impediu a abertura do boleto.');
      return;
    }

    boletoWindow.document.write('<p>Carregando boleto...</p>');

    const visualizarBoletosRequest = this.todasParcelasSelecionado
      ? this.financeiroService.visualizarBoletosTodasParcelas(
          [conta.boletoId], alunoIds, this.anoSelecionado.id)
      : this.financeiroService.visualizarBoletos(
          [conta.boletoId], alunoIds, this.anoSelecionado.id);

    visualizarBoletosRequest.subscribe({
      next: (response: HttpResponse<BoletoVisualizacao[]>) => {
        const boleto = response.body?.[0];

        if (!boleto) {
          boletoWindow.close();
          alert('Boleto nao encontrado para esta parcela.');
          return;
        }

        this.abrirConteudoBoleto(boleto, boletoWindow);
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao buscar boleto:', err);
        boletoWindow.close();
        alert('Nao foi possivel abrir o boleto no momento.');
      }
    });
  }

  getClasseBoleto(conta: ContaReceberPortal): string {
    switch (this.getCorIconeBoleto(conta)) {
      case 'Verde': return 'documento-action--boleto-verde';
      case 'Azul':  return 'documento-action--boleto-azul';
      default:      return 'documento-action--boleto-cinza';
    }
  }

  getTituloBoleto(conta: ContaReceberPortal): string {
    return this.getCorIconeBoleto(conta) === 'Cinza Claro' ? 'Sem boleto' : 'Boleto disponível';
  }

  exibirIconeNotaFiscal(conta: ContaReceberPortal): boolean {
    return !!conta.notaFiscalId
      && conta.notaFiscalId > 0
      && conta.statusProcessamentoNotaFiscal === '100';
  }

  private getCorIconeBoleto(conta: ContaReceberPortal): CorIconeBoleto {
    if (conta.boletoId > 0 && this.isContaQuitada(conta.statusPagamento)) {
      return 'Verde';
    }

    if (conta.boletoId > 0) {
      return 'Azul';
    }

    return 'Cinza Claro';
  }

  isContaQuitada(status: ContaParcelaStatusPagamento): boolean {
    return status === ContaParcelaStatusPagamento.Quitada;
  }

  get todasContasSelecionadas(): boolean {
    return this.contas.length > 0 && this.contas.every(
      conta => this.contasSelecionadas.has(conta.contaParcelaId)
    );
  }

  alternarConta(contaParcelaId: number, selecionada: boolean) {
    if (selecionada) {
      this.contasSelecionadas.add(contaParcelaId);
      return;
    }

    this.contasSelecionadas.delete(contaParcelaId);
  }

  alternarTodasContas(selecionadas: boolean) {
    this.contasSelecionadas.clear();

    if (selecionadas) {
      this.contas.forEach(conta => this.contasSelecionadas.add(conta.contaParcelaId));
    }
  }

  selecionarParaPagamento(contaParcelaId: number) {
    this.contasSelecionadas.add(contaParcelaId);
  }

  getClasseStatus(conta: ContaReceberPortal): string {
    if (this.isContaVencida(conta)) {
      return 'status-pill--danger';
    }

    switch (conta.statusPagamento) {
      case ContaParcelaStatusPagamento.Quitada:
        return 'status-pill--success';
      case ContaParcelaStatusPagamento.Ativa:
        return 'status-pill--warning';
      case ContaParcelaStatusPagamento.Retido:
        return 'status-pill--info';
      case ContaParcelaStatusPagamento.Estornado:
      case ContaParcelaStatusPagamento.ErroPagamento:
        return 'status-pill--danger';
      default:
        return 'status-pill--secondary';
    }
  }

  getTextoStatusPagamento(conta: ContaReceberPortal): string {
    if (this.isContaVencida(conta)) {
      return 'Vencido';
    }

    switch (conta.statusPagamento) {
      case ContaParcelaStatusPagamento.Ativa:
        return 'Aberto';
      case ContaParcelaStatusPagamento.Quitada:
        return 'Quitada';
      case ContaParcelaStatusPagamento.Cancelada:
        return 'Cancelada';
      case ContaParcelaStatusPagamento.Estornado:
        return 'Estornado';
      case ContaParcelaStatusPagamento.Retido:
        return 'Retido';
      case ContaParcelaStatusPagamento.ErroPagamento:
        return 'Erro pagamento';
      default:
        return 'Desconhecido';
    }
  }

  private isContaVencida(conta: ContaReceberPortal): boolean {
    if (conta.statusPagamento !== ContaParcelaStatusPagamento.Ativa || !conta.dataVencimento) {
      return false;
    }

    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    const dataAtual = `${ano}-${mes}-${dia}`;
    const dataVencimento = conta.dataVencimento.substring(0, 10);

    return dataVencimento < dataAtual;
  }

  getDescricaoAnoTurma(ano: AnosLetivos | null | undefined): string {
    if (!ano) return '';
    const turma = ano.turmaDescricao || ano.serie || '';
    return turma ? `${ano.descricao} - ${turma}` : ano.descricao;
  }

  getTurmaAtual(): string {
    return this.anoSelecionado?.turmaDescricao || this.anoSelecionado?.serie || this.alunoSelecionado?.serie || '';
  }

  getDescricaoSelecionada(): string {
    if (this.todasParcelasSelecionado) {
      return 'Consolidado de mensalidades, matrículas e material didático';
    }
    return this.getTurmaAtual();
  }

  private abrirConteudoBoleto(boleto: BoletoVisualizacao, boletoWindow: Window) {
    if (boleto.pdfBase64?.trim()) {
      try {
        const pdfUrl = this.criarPdfUrl(boleto.pdfBase64);
        boletoWindow.location.href = pdfUrl;
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        return;
      } catch (err) {
        console.error('Erro ao decodificar PDF do boleto:', err);
        boletoWindow.close();
        alert('Nao foi possivel gerar o PDF do boleto.');
        return;
      }
    }

    if (boleto.html?.trim()) {
      boletoWindow.document.open();
      boletoWindow.document.write(boleto.html);
      boletoWindow.document.close();
      return;
    }

    boletoWindow.close();
    alert('Boleto sem conteudo disponivel.');
  }

  private criarPdfUrl(pdfBase64: string): string {
    const base64 = pdfBase64.includes(',')
      ? pdfBase64.split(',').pop() ?? ''
      : pdfBase64;

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

    return URL.createObjectURL(pdfBlob);
  }

  private obterAlunoIdsConsultaAtual(): number[] {
    const alunoIds = this.todasParcelasSelecionado
      ? this.alunos.map(aluno => aluno.id)
      : this.alunoSelecionado ? [this.alunoSelecionado.id] : [];

    return Array.from(new Set(alunoIds.filter(id => id > 0)));
  }

  private agruparAlunosPorId(alunos: AlunoComAnosLetivos[]): AlunoComAnosLetivos[] {
    const alunosMap = new Map<number, AlunoComAnosLetivos>();

    for (const aluno of alunos ?? []) {
      const existente = alunosMap.get(aluno.id);
      const anosDoAluno = this.normalizarAnosLetivos(aluno);

      if (!existente) {
        alunosMap.set(aluno.id, { ...aluno, anosLetivos: this.ordenarAnosLetivos(anosDoAluno) });
        continue;
      }

      existente.anosLetivos = this.ordenarAnosLetivos([
        ...(existente.anosLetivos ?? []),
        ...anosDoAluno
      ]);

      if (!existente.serie && aluno.serie) {
        existente.serie = aluno.serie;
      }
    }

    return Array.from(alunosMap.values());
  }

  private normalizarAnosLetivos(aluno: AlunoComAnosLetivos): AnosLetivos[] {
    return (aluno.anosLetivos ?? []).map((ano) => ({
      ...ano,
      turmaDescricao: ano.turmaDescricao || ano.serie || aluno.serie,
      serie: ano.serie || ano.turmaDescricao || aluno.serie,
      matriculaId: ano.matriculaId || aluno.matriculaId
    }));
  }

  private ordenarAnosLetivos(anos: AnosLetivos[]): AnosLetivos[] {
    const anosMap = new Map<number, AnosLetivos>();

    for (const ano of anos ?? []) {
      const existente = anosMap.get(ano.id);

      if (!existente) {
        anosMap.set(ano.id, { ...ano });
        continue;
      }

      anosMap.set(ano.id, {
        ...existente, ...ano,
        turmaDescricao: existente.turmaDescricao || ano.turmaDescricao,
        serie: existente.serie || ano.serie,
        matriculaId: existente.matriculaId || ano.matriculaId
      });
    }

    return Array.from(anosMap.values()).sort((a, b) => this.obterOrdemAno(b) - this.obterOrdemAno(a));
  }

  private obterOrdemAno(ano: AnosLetivos): number {
    const match = ano?.descricao?.match(/\d{4}/);
    return match ? Number(match[0]) : Number(ano?.id ?? 0);
  }
}